import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, ApiError, type User } from "../api";
import { fmt } from "../constants";
import { btnPrimary } from "../icons";
import {
  AuthLegalFooter,
  BackButton,
  Caption,
  DESKTOP_CARD,
  Field,
  Subtitle,
  Title,
  mobileWrap,
} from "./auth-ui";

interface Props {
  isDesktop: boolean;
  onSignedUp: (user: User) => void;
  onSwitchToLogin: () => void;
  onOpenPrivacy: () => void;
  onOpenTerms: () => void;
}

const USERNAME_RE = /^[a-z0-9_]{3,20}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function SignupScreen({
  isDesktop,
  onSignedUp,
  onSwitchToLogin,
  onOpenPrivacy,
  onOpenTerms,
}: Props) {
  const { t } = useTranslation();
  const [step, setStep] = useState<0 | 1>(0);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [budget, setBudget] = useState("");

  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const emailError = useMemo(() => {
    if (!email) return null;
    if (!EMAIL_RE.test(email.trim())) return t("errors.validEmail");
    return null;
  }, [email, t]);

  const passwordError = useMemo(() => {
    if (!password) return null;
    if (password.length < 8) return t("errors.passwordMin");
    return null;
  }, [password, t]);

  const usernameError = useMemo(() => {
    if (!username) return null;
    if (username.length < 3) return t("errors.usernameMin");
    if (!USERNAME_RE.test(username)) return t("errors.usernameFormat");
    return null;
  }, [username, t]);

  const budgetNum = parseFloat(budget) || 0;
  const budgetError =
    budget && budgetNum <= 0 ? t("errors.budgetGreater") : null;

  const step0Ok =
    email.length > 0 &&
    password.length >= 8 &&
    !emailError &&
    !passwordError;
  const step1Ok =
    username.length >= 3 &&
    !usernameError &&
    budgetNum > 0 &&
    !budgetError;

  const submit = async () => {
    if (!step1Ok || submitting) return;
    setSubmitting(true);
    setServerError(null);
    try {
      const user = await api.signup({
        email: email.trim(),
        password,
        username,
        budget: budgetNum,
      });
      onSignedUp(user);
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.code === "EMAIL_TAKEN") {
          setServerError(t("errors.emailTaken"));
          setStep(0);
        } else if (e.code === "USERNAME_TAKEN") {
          setServerError(t("errors.usernameTaken"));
        } else {
          setServerError(e.message);
        }
      } else {
        setServerError(e instanceof Error ? e.message : t("errors.signUpFailed"));
      }
      setSubmitting(false);
    }
  };

  return (
    <div style={isDesktop ? DESKTOP_CARD : mobileWrap("40px 24px 30px")}>
      {step === 1 && <BackButton onClick={() => setStep(0)} />}

      <div style={{ display: "flex", gap: 6, marginBottom: 32 }}>
        {[0, 1].map((i) => (
          <div
            key={i}
            style={{
              flex: 1,
              height: 3,
              borderRadius: 999,
              background: i <= step ? "var(--ink)" : "var(--line)",
              transition: "background .25s",
            }}
          />
        ))}
      </div>

      {step === 0 && (
        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Caption>{t("auth.step1of2")}</Caption>
          <Title>{t("auth.createTitle")}</Title>
          <Subtitle>{t("auth.createSubtitle")}</Subtitle>

          <Field
            label={t("auth.email")}
            type="email"
            inputMode="email"
            value={email}
            onChange={(v) => {
              setEmail(v);
              setTouched((tk) => ({ ...tk, email: true }));
              setServerError(null);
            }}
            placeholder={t("auth.emailPlaceholder")}
            autoFocus
            error={touched.email ? emailError : null}
          />

          <Field
            label={t("auth.createPassword")}
            type="password"
            value={password}
            onChange={(v) => {
              setPassword(v);
              setTouched((tk) => ({ ...tk, password: true }));
              setServerError(null);
            }}
            placeholder={t("auth.passwordPlaceholderHint")}
            error={touched.password ? passwordError : null}
            hint={
              touched.password || !password ? undefined : t("auth.passwordPlaceholderHint")
            }
          />

          {serverError && (
            <div style={{ marginTop: 4, marginBottom: 8, fontSize: 13, color: "var(--danger)" }}>
              {serverError}
            </div>
          )}

          <div style={{ flex: 1 }} />

          <button
            disabled={!step0Ok}
            onClick={() => step0Ok && setStep(1)}
            style={btnPrimary(step0Ok)}
          >
            {t("auth.continue")}
          </button>

          <button
            onClick={onSwitchToLogin}
            style={{
              marginTop: 14,
              background: "transparent",
              border: "none",
              color: "var(--ink-2)",
              fontSize: 14,
              fontWeight: 500,
              textAlign: "center",
            }}
          >
            {t("auth.alreadyAccountPrefix")}{" "}
            <span style={{ color: "var(--ink)", fontWeight: 600 }}>{t("auth.signIn")}</span>
          </button>

          <AuthLegalFooter onOpenPrivacy={onOpenPrivacy} onOpenTerms={onOpenTerms} />
        </div>
      )}

      {step === 1 && (
        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Caption>{t("auth.step2of2")}</Caption>
          <Title>{t("auth.lastDetailsTitle")}</Title>
          <Subtitle>{t("auth.lastDetailsSubtitle")}</Subtitle>

          <Field
            label={t("auth.username")}
            value={username}
            onChange={(v) => {
              setUsername(v.toLowerCase().replace(/\s/g, ""));
              setTouched((tk) => ({ ...tk, username: true }));
              setServerError(null);
            }}
            placeholder={t("auth.usernamePlaceholder")}
            autoFocus
            error={touched.username ? usernameError : null}
            hint={
              touched.username || !username
                ? undefined
                : t("auth.usernameHint")
            }
            prefix={<span style={{ color: "var(--muted)", fontSize: 16 }}>@</span>}
          />

          <Field
            label={t("auth.monthlyBudget")}
            type="number"
            inputMode="decimal"
            value={budget}
            onChange={(v) => {
              setBudget(v);
              setTouched((tk) => ({ ...tk, budget: true }));
            }}
            placeholder="0"
            error={touched.budget ? budgetError : null}
            suffix={<span style={{ color: "var(--muted)", fontSize: 16 }}>L</span>}
          />

          <div style={{ display: "flex", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
            {[50000, 80000, 120000, 150000, 200000].map((v) => (
              <button
                key={v}
                onClick={() => {
                  setBudget(String(v));
                  setTouched((tk) => ({ ...tk, budget: true }));
                }}
                style={{
                  padding: "8px 14px",
                  borderRadius: 999,
                  fontSize: 13,
                  fontWeight: 500,
                  background: budgetNum === v ? "var(--ink)" : "var(--surface)",
                  color: budgetNum === v ? "#fff" : "var(--ink-2)",
                  border:
                    "1px solid " +
                    (budgetNum === v ? "var(--ink)" : "var(--line)"),
                }}
              >
                {fmt(v)}
              </button>
            ))}
          </div>

          {serverError && (
            <div style={{ marginTop: 12, fontSize: 13, color: "var(--danger)" }}>
              {serverError}
            </div>
          )}

          <div style={{ flex: 1 }} />

          <button
            disabled={!step1Ok || submitting}
            onClick={submit}
            style={btnPrimary(step1Ok && !submitting)}
          >
            {submitting ? t("auth.creating") : t("auth.createButton")}
          </button>

          <div
            style={{
              marginTop: 14,
              fontSize: 12,
              lineHeight: 1.5,
              color: "var(--muted)",
              textAlign: "center",
            }}
          >
            {t("auth.termsAgreePrefix")}{" "}
            <button onClick={onOpenTerms} style={legalLinkStyle}>
              {t("auth.termsOfService")}
            </button>{" "}
            {t("auth.and")}{" "}
            <button onClick={onOpenPrivacy} style={legalLinkStyle}>
              {t("auth.privacyPolicy")}
            </button>
            .
          </div>
        </div>
      )}
    </div>
  );
}

const legalLinkStyle: React.CSSProperties = {
  background: "transparent",
  border: "none",
  padding: 0,
  color: "var(--ink)",
  fontSize: 12,
  fontWeight: 500,
  textDecoration: "underline",
  cursor: "pointer",
};
